/* eslint-disable react-hooks/exhaustive-deps */
import { LayerGroup, Paperclip, PlusCircle, Refresh, Trash } from '@styled-icons/fa-solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import edit_notifications from '../../../src/images/edit_notifications.png';
import {
  amendDeclaration,
  getLoadStatus,
  getNeeNorVATDetails,
  postListLoadStatus,
  addSubtask,
  deleteSubtask,
} from '../../api/v2/load';
import '../../styles/gridEdit.css';
import { ImportType } from '../../utils/enums';
import { systemReferenceHasAnyStatusErrors, validateSystemReferenceFromStatus } from '../../utils/loads';
import { parseClearanceDate } from '../../utils/stringUtils';
import { postAdditionalEPOFile, updateTransactionLoadUploadedDocumentStatus } from '../apiCalls';
import ArrivalConfirmation from './Modals/ArrivalConfirmation';
import ConfirmationModal from '../ConfirmationModal';
import DataTable from '../DataTable';
import { useDataTableHandle } from '../DataTable/DataTable';
import { DeclarationsButton } from '../DeclarationsButton';
import ResubmissionModal from './ResubmissionModal';
import { GoogleAnalyticsContext } from '../GoogleAnalytics';
import LoadStatusIcon from '../LoadStatusIcon/LoadStatusIcon';
import '../main.css';
import PermissionDeniedModal from './PermissionDeniedModal/PermissionDeniedModal';
import OverLayWrapper from '../Overlay';
import { useSecurity } from '../Security/security';
import { StoreContext } from '../Store';
import ToastNotification from '../Toast/ToastNotification';
import AddSubtaskModal from './AddSubtaskModal';
import AuthModal from './AuthModal';
import LoadHeader from './LoadHeader';
import DeclarationAmendmentModal from './Modals/DeclarationAmendmentModal';
import QAErrors from './QAChecklistTab/QAErrors/QAErrors';
import { convertToGbp, getDutyRatesFromItems, getIsVatLiable } from './QAChecklistTab/utils';
import { useDocumentSubmit } from './queryHooks/useDocumentSubmit';
import { useExchangeRates } from './queryHooks/useExchangeRates';
import { useQaItemsActions } from './queryHooks/useQaItemsApis';

const GENERIC_ERROR =
  'An error occurred while resubmitting the load. Please try again. If the problem persists, please contact IT.';
const SUBTASK_ADD_SUCCESS_MSG = 'Subtask added successfully!';
const SUBTASK_INVALID_ROUTE_MSG = 'Invalid route for Subtask';

const LoadStatus = (props) => {
  const { id } = useParams();

  const [requestFailed, setRequestFailed] = useState(false);
  const loadNumber = props.invoicenumber;
  const isReadOnly = !!props?.isReadOnly;
  const store = useContext(StoreContext);
  const ga = useContext(GoogleAnalyticsContext);
  const productItemsList = store.validatedRowsReadyToSubmit[0];
  const exchangeRate = store.exchangeRate[0];
  const { fetchCommodityDutyRates } = useQaItemsActions();
  const { fetchExchangeRate } = useExchangeRates(store.currencyOptions[0]);

  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [error, setError] = useState();
  const [showResubmissionModal, setShowResubmissionModal] = useState(false);
  const [systemReferenceHasErrors, setSystemReferenceHasErrors] = useState();
  const [hasExciseGoods, setHasExciseGoods] = useState(false);
  const [hasOtherDuty, setHasOtherDuty] = useState(false);
  const [liability, setLiability] = useState(false);
  const [vatType, setVatType] = useState('');
  const consignee = store.consignee[0];
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] = useState(false);
  const [onLoginConfirmType, setOnLoginConfirmType] = useState('');
  const [loadingDutyData, setLoadingDutyData] = useState(false);
  const [dutyRatesError, setDutyRatesError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const [openAmendmentModal, setOpenAmendmentModal] = useState(false);
  const queryClient = useQueryClient();

  const showStatusToast = (message, variant = 'success') => {
    setToastConfig({ type: variant === 'danger' ? 'error' : 'success', message });
    setShowToast(true);
  };

  const amendDeclarationMutation = useMutation({
    mutationFn: (load) => amendDeclaration(load),
    onSuccess: async () => {
      showStatusToast('Flagged for Amendment!');
      await refreshGrid();
    },
    onError: (error) => {
      console.error('Error amending declaration:', error);
      showStatusToast('Error amending declaration.', 'danger');
    },
  });

  const uploadFile = useMutation({
    mutationFn: (data) => postAdditionalEPOFile(data),
  });

  const updateDocumentStatus = useMutation({
    mutationFn: (transactionID) => updateTransactionLoadUploadedDocumentStatus(transactionID),
  });

  const postListLoadMutation = useMutation({
    mutationFn: (payload) => postListLoadStatus(payload),
  });

  const addSubtaskMutation = useMutation({
    mutationFn: ({ id, subTask, isEpo }) => addSubtask(id, subTask, isEpo),
  });

  const deleteSubtaskMutation = useMutation({
    mutationFn: (id) => deleteSubtask(id),
  });

  const { submitC21, submitEAD, submitFFD, submitSDI, submitSFD, submitIEImport, submitIEExport } = useDocumentSubmit();

  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);
  const [sfdRow, setSfdRow] = useState(null);

  const [resubmitError, setResubmitError] = useState(null);

  const headerData = props.headerData;

  const { validateRole } = useSecurity();
  const isDutyUser = validateRole({ isDutyUser: true });
  const hasDutyOrExcise = liability || hasExciseGoods || hasOtherDuty;
  const canProceedWithoutDutyWarning = isDutyUser || !hasDutyOrExcise;

  useEffect(() => {
    async function fetchData() {
      try {
        await refreshGrid();
      } catch {
        setRequestFailed(true);
      }
    }
    fetchData();
  }, []);

  const refreshGrid = async () => {
    try {
      const { data } = await getLoadStatus(loadNumber);
      store.gridItems[1](data);
      const sfd = data.find((item) => item['Additional Information'] === ImportType.SFD);
      setSfdRow(sfd);

      if (sfd?.CustomsAcceptanceDate) {
        const { date, time } = parseClearanceDate(sfd.CustomsAcceptanceDate);
        store.portArrivalDate[1](date);
        store.portArrivalTime[1](time);
      } else {
        store.portArrivalDate[1](null);
        store.portArrivalTime[1](null);
      }
    } catch {
      setRequestFailed(true);
    }
  };

  const handleSdiClick = (item) => {
    if (canProceedWithoutDutyWarning) {
      handleOpenModal(item);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleOpenModal = (item) => {
    setModalItem(item);
    setShowArrivalModal(true);
  };

  const handleSubmit = async () => {
    setError();
    const findDoc = (type) => dataHandle.current.modifiedData.find((row) => row['Additional Information'] === type);

    const preSubmitChecks = [
      { type: 'SDI', message: 'Please request SDI (see button) before submitting a system reference number' },
      { type: 'SFD', message: 'Please request an SFD (see button) before submitting MUCR/UCN reference number' },
      { type: 'FFD', message: 'Please request an FFD (see button) before submitting MUCR/UCN reference number' },
      { type: 'C21', message: 'Please request an C21 (see button) before submitting MUCR/UCN reference number' },
    ];

    const hasBlockingDoc = preSubmitChecks.some(({ type, message }) => {
      const doc = findDoc(type);
      if (doc && doc.Status === 'Submitted' && !!doc['System Ref No.']) {
        setError(message);
        return true;
      }
      return false;
    });
    if (hasBlockingDoc) return;

    const updatedCells = [];
    let i = 0;
    for (const row of dataHandle.current.modifiedData) {
      const system = row['System'];
      const refNo = row['System Ref No.'].toUpperCase();

      updatedCells.push({
        colname: 'System',
        rowid: row.id,
        staterow: i++,
        value: system,
      });

      updatedCells.push({
        colname: 'SystemRefNo',
        rowid: row.id,
        staterow: i++,
        value: refNo,
      });
    }
    const invLinked = dataHandle.current.modifiedData.find((row) => {
      return row['Additional Information'] === 'Inventory Release';
    });
    if (invLinked) {
      updatedCells.push({
        colname: 'PortArrived',
        rowid: invLinked.id,
        staterow: i++,
        value: (invLinked.PortArrived?.id ? invLinked.PortArrived.id : invLinked.PortArrived) ?? null,
      });
    }

    try {
      const data = await postListLoadMutation.mutateAsync(updatedCells);
      if (data[0].completed === 'Updated') {
        store.saved[1](data);
        await refreshGrid();
      }
    } catch (err) {
      console.error(err);
      alert('Error saving to DB');
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchDutyRates = async () => {
      setLoadingDutyData(true);
      const vatAdjustment = Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.amount || 0);
      const vatCurrency = await fetchExchangeRate(
        Number(store.adjustments[0]?.find((adj) => adj.name === 'vatAdjustment')?.currency || 0),
        store.date[0]
      );
      const vatAdjustmentGbp = Number(convertToGbp(vatAdjustment, vatCurrency));

      const dutyRate = await getDutyRatesFromItems(
        productItemsList,
        exchangeRate,
        vatAdjustmentGbp,
        store.RoiImport[0],
        null,
        fetchCommodityDutyRates
      );
      if (cancelled) return;
      const dutyData = dutyRate.data;
      setDutyRatesError(dutyRate.error);

      if (dutyData?.length) {
        setHasExciseGoods(dutyData.some((duty) => !!duty.excise));
        setHasOtherDuty(dutyData.some((duty) => !!duty.additionalDuties));

        const isPva = vatType === 'PVA';
        const hasDutyBeenSaved = !productItemsList.every((product) => product.dutyLiability === null);
        let hasLiability = false;
        if (hasDutyBeenSaved) {
          hasLiability = productItemsList.some((row) => {
            if (isPva) {
              return !['-', '0', 0].includes(row.dutyLiability) && !!row.dutyLiability;
            }
            return (
              (!['-', '0', 0].includes(row.dutyLiability) && !!row.dutyLiability) ||
              (!['-', '0', 0].includes(row.vatLiability) && !!row.vatLiability)
            );
          });
        } else {
          const hasDuty = dutyData.some((item) => !!item.dutyLiability);
          const hasVat = productItemsList.some((item) => getIsVatLiable(item.VAT) && item.itemValue);

          hasLiability = isPva ? hasDuty : hasDuty || hasVat;
        }

        setLiability(hasLiability);
      }
      if (!cancelled) {
        setLoadingDutyData(false);
      }
    };

    if (!productItemsList?.length || !exchangeRate) return;
    if (consignee === undefined || consignee === null || consignee === '') return;

    fetchDutyRates();

    return () => {
      cancelled = true;
    };
  }, [productItemsList, exchangeRate, vatType, consignee]);

  useEffect(() => {
    if (consignee === undefined || consignee === null || consignee === '') return;

    let cancelled = false;

    const fetchVatData = async () => {
      const data = await queryClient.fetchQuery({
        queryKey: ['getNeeNorVATDetails', consignee],
        queryFn: () => getNeeNorVATDetails(consignee),
      });
      if (cancelled) return;
      setVatType(data?.[0]?.vatMessage ?? '');
    };

    fetchVatData();

    return () => {
      cancelled = true;
    };
  }, [consignee]);

  const authConfirm = () => {
    if (onLoginConfirmType === 'doc') {
      handleDocSubmit();
    }

    if (onLoginConfirmType === 'c21') {
      handleC21Submit();
    }
  };

  const handleDocClick = () => {
    if (canProceedWithoutDutyWarning) {
      handleDocSubmit();
    } else {
      setShowLoginModal(true);
      setOnLoginConfirmType('doc');
    }
  };

  const handleDocSubmit = async () => {
    const sfdRequest = {
      id: loadNumber,
    };

    try {
      await submitSFD.mutateAsync(sfdRequest);
      await refreshGrid();
    } catch (err) {
      console.error(err);
      alert('Error sending SFD');
    }
    setShowLoginModal(false);
  };

  const handleC21Click = () => {
    if (canProceedWithoutDutyWarning) {
      handleC21Submit();
    } else {
      setShowLoginModal(true);
      setOnLoginConfirmType('c21');
    }
  };

  const handleC21Submit = async () => {
    const c21Request = {
      id: loadNumber,
    };
    try {
      await submitC21.mutateAsync(c21Request);
      await refreshGrid();
    } catch (err) {
      console.error(err);
      alert('Error sending C21');
    }
    setShowLoginModal(false);
  };

  const handleFFDClick = () => {
    if (canProceedWithoutDutyWarning) {
      handleFFDSubmit();
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleFFDSubmit = async () => {
    const ffdRequest = {
      id: loadNumber,
    };
    try {
      await submitFFD.mutateAsync(ffdRequest);
      await refreshGrid();
    } catch (err) {
      console.error(err);
      alert('Error sending FFD');
    }
  };

  const handleModalSubmit = async () => {
    const sdiRequest = {
      id: loadNumber,
      date: store.portArrivalDate[0],
      time: store.portArrivalTime[0],
    };
    try {
      await submitSDI.mutateAsync(sdiRequest);
      await refreshGrid();
    } catch (err) {
      console.error(err);
      alert('Error sending SDI');
    }
  };

  const dataHandle = useDataTableHandle();

  const handlePortArrived = (item) => {
    return item['Form type'] === 'Inventory linked release' && item['showVesselArrived'] !== false;
  };

  const disableSystemReference = (item) => {
    //For the field to be enable, item shouldn't be locked AND it shouldn't be read only.
    return !item.locked && !isReadOnly;
  };

  const setGATagForResubmission = (declarationType) => {
    if (ga.gaInitialized && declarationType) {
      window?.gtag?.('event', `${declarationType} resubmitted`, {
        event_category: 'Resubmit Declaration',
      });
    }
  };

  const submitDeclaration = async () => {
    try {
      const requiresDate = showResubmissionModal == 'SDI';
      const requiresCurrency = showResubmissionModal == 'IE Import';

      const payload = {
        id: loadNumber,
        resubmission: true,
        ...(requiresDate && {
          date: new Date().toISOString().split('T')[0],
          time: store.portArrivalTime[0] || '12:00',
        }),
        ...(requiresCurrency && {
          currencyCode: headerData?.Currency,
        }),
      };

      switch (showResubmissionModal) {
        case 'SDI':
          await submitSDI.mutateAsync(payload);
          break;
        case 'SFD':
          await submitSFD.mutateAsync(payload);
          break;
        case 'C21':
          await submitC21.mutateAsync(payload);
          break;
        case 'FFD':
          await submitFFD.mutateAsync(payload);
          break;
        case 'GB Export':
          await submitEAD.mutateAsync(payload);
          break;
        case 'IE Import':
          await submitIEImport.mutateAsync(payload);
          break;
        case 'IE Export':
          await submitIEExport.mutateAsync(payload);
          break;
        default:
          alert('No declaration type selected for resubmission: ' + showResubmissionModal);
          return;
      }
      setGATagForResubmission(showResubmissionModal);
      await refreshGrid();
    } catch (err) {
      console.error('Resubmit Error:', err);

      try {
        const error = JSON.parse(err.message);
        setResubmitError(error);
      } catch (parseError) {
        console.error('Parse Error:', parseError);
        setResubmitError({
          message: GENERIC_ERROR,
        });
      }
    }
  };

  const getRefreshMessage = (item) => {
    if (item?.['System Ref No.']) {
      const systemName = item?.['System'] || 'Descartes';
      return `You are unable to submit this declaration as a response has been received from ${systemName}.`;
    } else if (!item?.resubmissionAvailable && item?.resubmissionDate1) {
      return 'You can only resubmit this declaration two times';
    } else return '';
  };

  const removeRow = async (docId) => {
    if (!docId) {
      showStatusToast('Cannot delete: Document ID is missing', 'danger');
      return;
    }
    try {
      const data = await deleteSubtaskMutation.mutateAsync(docId);
      if (data) {
        store.gridItems[1](data);
        await refreshGrid();
      } else {
        // If deleteSubtask doesn't return data, just refresh
        await refreshGrid();
      }
      showStatusToast('Subtask deleted successfully!');
    } catch (err) {
      console.error('Error deleting subtask:', err);
      showStatusToast('Error deleting subtask. Please try again.', 'danger');
    }
  };

  const handleAddSubtask = async (subtaskData) => {
    try {
      // Must use subTasktId (the ID from API), not the type string
      if (!subtaskData.subTasktId) {
        alert('Error: Subtask ID is missing. Please try again.');
        return;
      }

      const subTaskId = subtaskData.subTasktId;
      const data = await addSubtaskMutation.mutateAsync({ id: loadNumber, subTask: subTaskId });

      if (data[0]?.status === 'Failed') {
        showStatusToast(SUBTASK_INVALID_ROUTE_MSG, 'danger');
        return;
      }
      showStatusToast(SUBTASK_ADD_SUCCESS_MSG);
      await refreshGrid();
    } catch (err) {
      console.error(err);
      showStatusToast('Error creating subtask.', 'danger');
    }
  };

  if (requestFailed) return <p>Failed!</p>;

  const onAmendDeclaration = async (id) => {
    try {
      await amendDeclarationMutation.mutateAsync(id);
    } catch (err) {
      console.error('Error amending declaration:', err);
      showStatusToast('Error amending declaration.', 'danger');
    }
  };

  const isSdiRequested = store.gridItems[0]?.some(
    (item) =>
      item['Additional Information'] === ImportType.SDI &&
      (item.Status === 'In-Progress' || item.Status === 'completed')
  );

  const getSavedSystemRefNo = (item, persistedRows = []) => {
    if (!item?.id) return item?.['System Ref No.'] ?? '';

    const persistedRow = persistedRows.find((row) => row.id === item.id);
    return persistedRow?.['System Ref No.'] ?? item?.['System Ref No.'] ?? '';
  };

  const createListLoadPayload = () => {
    const updatedCells = [];
    let i = 0;

    for (const row of dataHandle.current.modifiedData) {
      const system = row['System'];
      const refNo = row['System Ref No.'].toUpperCase();

      updatedCells.push({
        colname: 'System',
        rowid: row.id,
        staterow: i++,
        value: system,
      });

      updatedCells.push({
        colname: 'SystemRefNo',
        rowid: row.id,
        staterow: i++,
        value: refNo,
      });
    }
    const invLinked = dataHandle.current.modifiedData.find((row) => {
      return row['Form type'] === 'Inventory linked release';
    });
    if (invLinked) {
      updatedCells.push({
        colname: 'PortArrived',
        rowid: invLinked.id,
        staterow: i++,
        value: (invLinked.PortArrived?.id ? invLinked.PortArrived.id : invLinked.PortArrived) ?? null,
      });
    }

    postListLoadPayload(updatedCells);
  };

  const postListLoadPayload = async (payload) => {
    try {
      const data = await postListLoadMutation.mutateAsync(payload);
      if (data[0].completed === 'Updated') {
        store.saved[1](data);
        await refreshGrid();
      }
    } catch (err) {
      console.error(err);
      alert('Error saving to DB');
    }
  };

  const processFile = async (file, transactionID, transactionType) => {
    try {
      const formData = new FormData();
      formData.append('documents', file);
      formData.append('documentNames', transactionType);
      formData.append('id', id);

      const response = await uploadFile.mutateAsync(formData);

      if (response.success) {
        setError();
        const uploadedDocumentData = new FormData();
        uploadedDocumentData.append('id', transactionID);
        const response = await updateDocumentStatus.mutateAsync(transactionID);

        if (response[0].completed === 'Updated') {
          createListLoadPayload();
          queryClient.invalidateQueries({ queryKey: ['documentTable'] });
        }
      } else {
        console.error('response.error: ', response);
        setError(`The following error occurred while processing the file: ${response.error}`);
      }
    } catch (err) {
      console.error(err);
      setError('We could not process your file, please review the file and try again.');
    }
  };
  const uploadDocument = (e, transactionID, transactionType) => {
    const acceptedFiles = e.accept;
    const fileExtension = e.files[0]?.name?.slice(-4)?.toLowerCase();
    const isUploadedDocumentValid = acceptedFiles.includes(fileExtension);
    if (isUploadedDocumentValid) {
      processFile(e.files[0], transactionID, transactionType);
    }
  };

  const renderDeclarationAmendmentIcon = (item) => {
    const alreadyFlagged = !!item.amendedDate || !!item.amendedRole || !!item.amendedUser;

    const savedSystemRefNo = getSavedSystemRefNo(item, store.gridItems[0] ?? []);
    const hasMrn = !!savedSystemRefNo;
    const disabled = !hasMrn || isSdiRequested || alreadyFlagged;

    const title = !hasMrn
      ? 'Declaration Amendment unavailable until MRN is added'
      : isSdiRequested
        ? 'Declaration Amendment unavailable after SDI submission'
        : alreadyFlagged
          ? 'Already flagged for amendment'
          : 'Declaration Amendment';

    return (
      <button
        type="button"
        className="declaration-amendment-button p-1"
        disabled={disabled}
        title={title}
        onClick={() => {
          if (!disabled) {
            onAmendDeclaration(item.id);
          }
        }}
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          filter: !hasMrn ? 'grayscale(100%)' : 'none',
        }}
      >
        <img src={edit_notifications} alt="Declaration Amendment" width="20" />
      </button>
    );
  };

  return (
    <div className="floating-box-newload loaditems">
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />
      <QAErrors exchangeRate={exchangeRate} dutyRatesError={dutyRatesError} />
      <AuthModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onSuccess={() => authConfirm()}
        title="Confirm Duty Override"
        mainMessage="You are attempting to submit this on a load with duty."
        instructionMessage="Please enter your password to confirm."
      />
      <PermissionDeniedModal show={showPermissionDeniedModal} onHide={() => setShowPermissionDeniedModal(false)} />
      <ResubmissionModal
        show={showResubmissionModal}
        onHide={() => setShowResubmissionModal(false)}
        submitDeclaration={submitDeclaration}
        referenceNo={headerData?.portalRef}
      />
      <div className="d-flex flex-row justify-content-between">
        <h1 className="floating-box-header-newload loads">Load Status</h1>
        <div className="d-flex flex-column gap-2 justify-content-between align-items-end mb-4">
          <LoadHeader
            portalRef={headerData?.portalRef}
            companyName={headerData?.customerName}
            eoriNumber={headerData?.eoriNumber}
          />
        </div>
      </div>
      <DataTable
        data={store.gridItems[0]}
        className="table-striped table-bordered"
        editable
        handle={dataHandle}
        onMutate={(localData) => {
          const allValid = systemReferenceHasAnyStatusErrors(localData);
          setSystemReferenceHasErrors(allValid);
        }}
      >
        <DataTable.Column
          colKey="Additional Information"
          header="Subtask"
          maxWidth="27ch"
          style={{ whiteSpace: 'wrap' }}
        />
        <DataTable.Headers className="bg-white" style={{ fontSize: '13px' }} />
        <DataTable.Column colKey="Country/Port" header="Location" />
        <DataTable.Column
          colKey="System Ref No."
          header="System Reference"
          className="m-2 edit-cell system-reference uppercase-text"
          maxWidth="20ch"
          editable={disableSystemReference}
          validator={validateSystemReferenceFromStatus}
        />
        <DataTable.Column
          colKey="PortArrived"
          header="Options"
          className="m-2 edit-cell"
          locked={(item) => {
            return item.locked;
          }}
          editable={handlePortArrived}
        >
          <DataTable.Select
            options={[
              { id: 1, data: 'No' },
              { id: 2, data: 'Yes' },
            ]}
            optionIdKey="id"
            optionDisplayKey="data"
            defaultOption="Vessel Arrived?"
            editable={!isReadOnly}
          />
        </DataTable.Column>
        <DataTable.Column colKey="Status" header="Status">
          {(item) => <LoadStatusIcon status={item} />}
        </DataTable.Column>
        <DataTable.Column header="Submit" style={{ whiteSpace: 'nowrap', width: '1px' }}>
          {(item) => (
            <DeclarationsButton
              C21={item.C21}
              SFD={item.SFD}
              SDI={item.SDI}
              FFD={item.FFD}
              submitUser={item.submitUser}
              refNumber={item.refNumberUser}
              InventoryLinked={item.InventoryLinked}
              Status={item.Status}
              formSubmitted={item.formSubmitted}
              handleOpenModal={() => handleSdiClick(item)}
              handleDocSubmit={() => handleDocClick()}
              handleC21Submit={() => handleC21Click()}
              handleFFDSubmit={() => handleFFDClick()}
              loading={loadingDutyData}
              editable={!isReadOnly}
            />
          )}
        </DataTable.Column>
        <DataTable.Column header="Actions" style={{ whiteSpace: 'nowrap', width: '1.5rem' }}>
          {(item) => {
            let paperclipInfo = { color: '', helperText: 'upload document' };

            if (item.uploadedDocument) {
              paperclipInfo = { color: 'text-success', helperText: 'Document already uploaded' };
            } else if (item['System Ref No.']) {
              paperclipInfo = { color: 'text-warning', helperText: 'Document not added' };
            }

            return (
              <div className="d-flex flex-row gap-2 justify-content-start align-items-center">
                {item.documentUpload && (
                  <button type="button" className="restore-btn p-1 h-100" title={paperclipInfo.helperText}>
                    <label name="documentUpload">
                      <Paperclip className={paperclipInfo.color} width="1rem" height="1rem" />
                      <input
                        type="file"
                        name="fileupload"
                        onChange={(e) => uploadDocument(e.target, item?.id, item['Additional Information'])}
                        accept=".pdf,.png"
                        style={{ width: 0 }}
                      />
                    </label>
                  </button>
                )}

                {item.resubmit && (
                  <OverLayWrapper
                    placement="top"
                    popoverContent={<p>{getRefreshMessage(item)}</p>}
                    show={getRefreshMessage(item).length}
                  >
                    <button
                      type="button"
                      className="invoice-delete-icon p-1 h-100"
                      onClick={() => setShowResubmissionModal(item?.['Additional Information'])}
                      disabled={!item?.resubmissionAvailable || isReadOnly}
                    >
                      <Refresh width="1rem" height="1rem" />
                    </button>
                  </OverLayWrapper>
                )}
                {item.history && (
                  <Link
                    to={`/DeclarationsHistory/${props?.headerData?.id}/${item?.id}`}
                    title="Declarations History"
                    className="p-1"
                  >
                    <LayerGroup className="text-secondary" width="1rem" height="1rem" />
                  </Link>
                )}

                <span>{item.amendment && renderDeclarationAmendmentIcon(item)}</span>
              </div>
            );
          }}
        </DataTable.Column>
        <DataTable.Column
          header="Delete"
          style={{ whiteSpace: 'nowrap', width: '2rem', textAlign: 'center' }}
          className="p-0"
        >
          {(item) => {
            const canDelete = item.deleteIcon;

            return (
              <button
                type="button"
                className="subtask-delete-button"
                onClick={() => {
                  if (item.id && canDelete) {
                    removeRow(item.id);
                  } else if (!item.id) {
                    showStatusToast('Cannot delete: Item ID is missing', 'danger');
                  }
                }}
                disabled={!canDelete || !item.id}
                title={
                  canDelete && item.id
                    ? 'Delete subtask'
                    : !item.id
                      ? 'Cannot delete: Missing item ID'
                      : 'Cannot delete this subtask'
                }
              >
                <Trash width="1rem" height="1rem" />
              </button>
            );
          }}
        </DataTable.Column>
      </DataTable>
      <AddSubtaskModal
        show={showAddSubtaskModal}
        onHide={() => setShowAddSubtaskModal(false)}
        onSubmit={handleAddSubtask}
        isEpo={false}
        hasTraderUploadedIpaffs={false}
      />
      <button type="button" className="invoice-add-btn green-text" onClick={() => setShowAddSubtaskModal(true)}>
        <PlusCircle width="1.4rem" height="1.4rem" />
        <span className="text-decoration-underline bold" style={{ fontSize: '1rem' }}>
          Add a Subtask
        </span>
      </button>
      <div className="d-flex flex-row py-3 px-3 mt-4 gap-3 border-top">
        <button type="button" className="stepper-button yellow" onClick={props.prevPage}>
          Items
        </button>
        <div className="flex-grow-1" />
        <div className="">
          {error && (
            <div className="alert-danger px-3 py-2 w-100 " style={{ borderRadius: '10px', textAlign: 'center' }}>
              {error}
            </div>
          )}
        </div>
        <div className="flex-grow-1" />
        <button type="button" className="stepper-button green" onClick={() => dataHandle.current.reset()}>
          Undo
        </button>
        <button type="button" className="stepper-button red" onClick={handleSubmit} disabled={systemReferenceHasErrors}>
          Update
        </button>
      </div>
      <ArrivalConfirmation
        show={showArrivalModal}
        onHide={() => {
          setShowArrivalModal(false);
          setModalItem(null);
        }}
        onConfirm={() => {
          if (sfdRow?.amendedDate) {
            setOpenAmendmentModal(true);
          } else {
            handleModalSubmit();
          }
        }}
        item={modalItem}
        hasSfd={!!sfdRow}
        sfdArrived={!!sfdRow?.CustomsAcceptanceDate}
      />
      <ConfirmationModal
        header="An error occurred"
        show={!!resubmitError}
        onHide={() => setResubmitError(null)}
        hideConfirm
      >
        {resubmitError?.error || GENERIC_ERROR}
      </ConfirmationModal>

      <DeclarationAmendmentModal
        onHide={() => setOpenAmendmentModal(false)}
        showModal={openAmendmentModal}
        jiraTicket={store.jiraTicket[0]}
        onConfirm={() => {
          handleModalSubmit();
          setOpenAmendmentModal(false);
        }}
      />
    </div>
  );
};

export default LoadStatus;
